/**
 * PhaseRulesBlock — dynamischer "Bedingungen"-Block für den Anfragen-Drawer.
 *
 * Zeigt alle in den Admin-Einstellungen (PB Stages) gepflegten Bedingungen
 * (pb_phase_rules), gruppiert nach Ziel-Phase, wertet sie gegen den geöffneten
 * PB aus (✓ erfüllt / ✗ offen) UND bietet die ausfüllbaren Felder direkt an.
 * Der Feldtyp kommt aus /v2/admin/phase-rule-fields:
 *   ref     → Admin-Auswahl (assigned_to)
 *   date    → Datums-Input
 *   value   → Text-Input
 *   boolean → Checkbox (Kündigung schriftlich eingegangen)
 *   derived → nicht ausfüllbar: die Bedingung ergibt sich aus anderen Daten
 *             (Verträge unterschrieben, Bilder hochgeladen, SIS durchgeführt,
 *             SGB5 erfasst, Tagesplan vorhanden)
 * Gespeichert wird je Feld einzeln. Die Phasen-Felder, die der v2-Profil-
 * Endpoint kennt (MemberProfileFields::additionalUserFields(), s. V2_USER_FIELDS),
 * gehen über PUT /v2/admin/profiles/{profileId} — nur dort hängt der
 * Kalender-Spiegel (AnamneseTerminSync) für `anamnese_termin_am`. Der
 * v1-Weg PUT /v1/admin/user/{id} setzte das Datum, legte aber keinen Termin
 * an (Befund Staging 22.09.2026). Alle übrigen Felder (und alles ohne
 * bekannte Profil-ID) laufen weiterhin über v1; API 1.0 bleibt unangetastet.
 *
 * Auswertung: maßgeblich ist GET /v2/admin/phase-rules/status/{userId} — die
 * abgeleiteten Bedingungen sind im Browser nicht berechenbar. Nur wenn dieser
 * Aufruf fehlschlägt, greift die lokale Näherung rulePasses(), die wie bisher
 * PhaseRuleService::passes() für users-Spalten spiegelt:
 *   not_empty → Wert vorhanden (nicht null/''/0/'0')
 *   equals    → String-Vergleich mit dem Regelwert
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getThemeColors } from '../../lib/theme';
import {
  v2Phases, v2PhaseRules, v1Users, v1Admins, sortAdminsByFirstName, selectableAdmins, primaryProfile,
  type PbPhase, type PhaseRule, type PhaseRuleField, type V1User, type V1Admin,
} from '../../lib/v1';
import { v2Members, type FamilyMemberInput } from '../../lib/familyAccounts';
import { FAMILY_ACCOUNT_FIELD_LABEL_KEYS } from '../../lib/familyAccountCompleteness';
import { toLocalDateKey } from '../../lib/localDate';
import { TERMIN_TIME_FIELDS, toTimeInput, type TerminDateField } from '../../lib/terminFields';
import { DateTimeInput, splitDateTime } from '../DateTimeInput';
import { DateInput } from '../DateInput';

/**
 * users-Spalten, die PUT /v2/admin/profiles/{id} entgegennimmt
 * (Auszug aus MemberProfileFields::additionalUserFields(), nur die im
 * Regelsystem ausfüllbaren Felder).
 */
const V2_USER_FIELDS = new Set([
  'startdatum', 'todestag', 'kuendigung_schriftlich',
  'anamnese_termin_am', 'beratungsgespraech_am',
  // Uhrzeit zu den Terminen (s. lib/terminFields.ts)
  'anamnese_termin_um', 'beratungsgespraech_um',
]);

/** Feldtypen, die der Block NICHT selbst ausfüllen kann. */
const DERIVED = 'derived';

function rulePasses(raw: V1User | null | undefined, rule: PhaseRule): boolean {
  if (!raw) return false;
  const value = (raw as unknown as Record<string, unknown>)[rule.field];
  if (rule.operator === 'equals') return String(value ?? '') === String(rule.value ?? '');
  return value !== null && value !== undefined && value !== '' && value !== 0 && value !== '0';
}

function describeRule(rule: PhaseRule, tr: (key: string, opts?: Record<string, unknown>) => string): string {
  return rule.operator === 'equals'
    ? tr('care.phase_rules.must_equal', { field: rule.field_label, value: rule.value })
    : tr('care.phase_rules.must_be_set', { field: rule.field_label });
}

/**
 * ISO-/Datetime-Wert → yyyy-mm-dd für <DateInput>.
 *
 * Bug (E2E Staging 22.09.2026): ein rohes `.slice(0, 10)` auf einem
 * UTC-Datetime-String (z.B. "2026-09-24T22:00:00.000000Z" für den 25.09.,
 * App-TZ Europe/Berlin) schneidet den UTC-Tag ab, nicht den lokalen — das
 * Feld zeigte nach Reload einen Tag zu früh, und erneutes Speichern hätte
 * den Termin verschoben. Nutzt denselben lokalen Konverter wie der Kalender
 * (siehe lib/localDate.ts), der reine Y-m-d-Strings unverändert lässt und
 * Datetime-Strings über die lokalen Date-Komponenten neu zusammensetzt.
 */
function toDateInput(v: unknown): string {
  if (typeof v !== 'string' || v.length < 10) return '';
  return toLocalDateKey(v);
}

export function PhaseRulesBlock({ dark, raw, onSaved }: {
  dark: boolean;
  raw: V1User | null | undefined;
  /** Wird nach jedem erfolgreichen Feld-Save mit dem gemergten User gerufen. */
  onSaved?: (updated: V1User) => void;
}) {
  const { t: tr } = useTranslation();
  const t = getThemeColors(dark);
  const [rules, setRules] = useState<PhaseRule[]>([]);
  const [phases, setPhases] = useState<PbPhase[]>([]);
  const [fieldTypes, setFieldTypes] = useState<Record<string, string>>({});
  const [admins, setAdmins] = useState<V1Admin[]>([]);
  // Lokale Overrides nach Saves — hält die Auswertung aktuell, auch wenn der
  // Parent `raw` nicht sofort neu setzt.
  const [local, setLocal] = useState<Record<string, unknown>>({});
  const [savingField, setSavingField] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Text-Input-Entwürfe (value-Felder speichern erst auf Blur/Enter).
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  // Serverseitige Auswertung je Regel-ID. undefined = (noch) nicht geladen.
  const [serverStatus, setServerStatus] = useState<Record<number, boolean> | null>(null);
  // Fehlende Pflichtfelder je Regel-ID (nur Familienkonto-Bedingung).
  const [serverMissing, setServerMissing] = useState<Record<number, string[]>>({});

  useEffect(() => {
    let alive = true;
    // allSettled: ein fehlschlagender Teil-Request (z. B. phase-rule-fields
    // hinter permission:menu.einstellungen) darf den Block nicht komplett
    // verschwinden lassen — Feldtypen fallen dann auf 'value'/Heuristik zurück.
    Promise.allSettled([v2PhaseRules.list(), v2Phases.list(), v2PhaseRules.fields()])
      .then(([r, p, f]) => {
        if (!alive) return;
        const active = r.status === 'fulfilled' ? r.value.filter(x => x.aktiv) : [];
        setRules(active);
        if (p.status === 'fulfilled') setPhases(p.value.slice().sort((a, b) => a.sort - b.sort));
        const types: Record<string, string> = f.status === 'fulfilled'
          ? Object.fromEntries(f.value.map(x => [x.field, x.type]))
          : {};
        setFieldTypes(types);
        // Admin-Liste nur laden, wenn eine ref-Bedingung existiert (Fallback-
        // Heuristik: assigned_to ist ein ref-Feld, auch ohne fields-Endpoint).
        if (active.some(x => (types[x.field] ?? (x.field === 'assigned_to' ? 'ref' : 'value')) === 'ref')) {
          v1Admins.list()
            .then(list => {
              if (!alive) return;
              setAdmins(sortAdminsByFirstName(list));
            })
            .catch(() => { /* Auswahl degradiert zu leer */ });
        }
      });
    return () => { alive = false; };
  }, []);

  // Bei Userwechsel lokale Overrides/Entwürfe verwerfen.
  useEffect(() => { setLocal({}); setDrafts({}); setError(null); }, [raw?.id]);

  /** Serverseitige Regel-Auswertung holen (auch nach jedem Save). */
  const loadStatus = React.useCallback(() => {
    const id = raw?.id;
    if (!id) { setServerStatus(null); return; }
    v2PhaseRules.status(id)
      .then(list => {
        setServerStatus(Object.fromEntries(list.map(x => [x.id, x.passes])));
        setServerMissing(Object.fromEntries(list.filter(x => x.missing?.length).map(x => [x.id, x.missing as string[]])));
      })
      // Fehlt die Berechtigung oder der Endpoint, bleibt die lokale Näherung.
      .catch(() => setServerStatus(null));
  }, [raw?.id]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const merged = raw ? ({ ...raw, ...local } as V1User) : raw;

  const save = (field: string, value: unknown) => saveMany({ [field]: value });

  /** Speichert ein oder mehrere Felder in EINEM Request (z. B. Datum + Uhrzeit). */
  const saveMany = async (patch: Record<string, unknown>) => {
    const fields = Object.keys(patch);
    if (!raw?.id || savingField || fields.length === 0) return;
    setSavingField(fields[0]);
    setError(null);
    try {
      const profileId = primaryProfile(raw)?.id;
      if (profileId && fields.every(f => V2_USER_FIELDS.has(f))) {
        await v2Members.update(profileId, patch as unknown as FamilyMemberInput);
      } else {
        await v1Users.update(raw.id, patch as Partial<V1User>);
      }
      const next = { ...local, ...patch };
      setLocal(next);
      onSaved?.({ ...raw, ...next } as V1User);
      // Der Save kann auch abgeleitete Bedingungen kippen — Status neu holen.
      loadStatus();
    } catch {
      setError(tr('pflege.edit.save_failed'));
    } finally {
      setSavingField(null);
    }
  };

  /** Maßgeblich ist der Server; nur ohne Status greift die lokale Näherung. */
  const isFulfilled = (rule: PhaseRule): boolean =>
    serverStatus?.[rule.id] ?? rulePasses(merged, rule);

  const currentPhase = Number(merged?.phase ?? 0);

  // Stage-abhängig: nur die Bedingungen der NÄCHSTEN Phase(n) anzeigen —
  // bereits erreichte Stages sind erledigt und bleiben ausgeblendet. Gezeigt
  // wird die nächste Phase mit Bedingungen (der unmittelbar anstehende Schritt).
  //
  // Maßgeblich ist die REIHENFOLGE (phase.sort), nicht die ID: eine später
  // angelegte Stage bekommt eine hohe ID, kann aber weit vorne stehen. Mit
  // `p.id > currentPhase` galten solche Stages fälschlich als „kommt noch"
  // bzw. bereits durchlaufene als anstehend (Bugreport 18.09.2026, gleiche
  // Ursache wie im Backend-PhaseRuleService).
  const currentSort = phases.find(p => p.id === currentPhase)?.sort;
  const isAhead = (p: PbPhase): boolean =>
    currentSort === undefined ? p.id > currentPhase : p.sort > currentSort;
  const upcoming = phases
    .filter(p => isAhead(p) && rules.some(r => r.phase_id === p.id));
  const phasesWithRules = upcoming.slice(0, 1);
  // Finale Stage: `upcoming` ist dann leer (keine Stage liegt nach der
  // aktuellen) — der Block blendet sich hier bereits selbst aus.
  if (phasesWithRules.length === 0) return null;

  const INPUT_STYLE: React.CSSProperties = {
    border: `1px solid ${t.border}`, borderRadius: 7,
    padding: '5px 8px', fontSize: 12, color: t.text,
    background: t.inputBg, fontFamily: 'Outfit, sans-serif', outline: 'none',
    width: '100%', boxSizing: 'border-box',
  };

  const renderInput = (rule: PhaseRule) => {
    const rawValue = (merged as unknown as Record<string, unknown>)?.[rule.field];
    // Heuristik-Fallback, falls der fields-Endpoint nicht verfügbar war.
    const type = fieldTypes[rule.field]
      ?? (rule.field === 'assigned_to' ? 'ref'
        : /(_am$|datum$|_at$)/.test(rule.field) ? 'date'
        : 'value');
    const busy = savingField === rule.field;

    if (type === DERIVED) {
      // Ergibt sich aus anderen Daten (Dokumente, Uploads, SIS-Freitexte,
      // SGB5, Tagesplan) — hier bewusst kein Eingabefeld.
      const missing = (serverMissing[rule.id] ?? []).filter(k => FAMILY_ACCOUNT_FIELD_LABEL_KEYS[k]);
      if (rule.field === 'familienkonto_komplett' && missing.length > 0) {
        return (
          <span data-testid="phase-rule-missing" style={{ fontSize: 11, color: '#b54708', lineHeight: 1.35 }}>
            {tr('care.phase_rules.family_account_missing', {
              fields: missing.map(k => tr(FAMILY_ACCOUNT_FIELD_LABEL_KEYS[k])).join(', '),
            })}
          </span>
        );
      }
      return (
        <span style={{ fontSize: 11, color: t.muted, lineHeight: '26px' }}>
          {tr('care.phase_rules.derived_note')}
        </span>
      );
    }

    if (type === 'boolean') {
      const checked = rawValue === true || rawValue === 1 || rawValue === '1';
      return (
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: t.text, lineHeight: '26px', cursor: busy ? 'default' : 'pointer' }}>
          <input
            aria-label={rule.field_label}
            type="checkbox"
            checked={checked}
            disabled={busy}
            onChange={e => void save(rule.field, e.target.checked)}
          />
          {checked ? tr('care.phase_rules.yes') : tr('care.phase_rules.no')}
        </label>
      );
    }

    if (type === 'ref') {
      return (
        <select
          aria-label={rule.field_label}
          value={rawValue != null ? String(rawValue) : ''}
          disabled={busy}
          onChange={e => void save(rule.field, e.target.value ? Number(e.target.value) : null)}
          style={INPUT_STYLE}
        >
          <option value="">{tr('care.phase_rules.select_placeholder')}</option>
          {selectableAdmins(admins, [rawValue != null ? Number(rawValue) : null]).map(a => (
            <option key={a.id} value={a.id}>
              {[a.vorname, a.nachname].filter(Boolean).join(' ') || a.email}
            </option>
          ))}
        </select>
      );
    }

    if (type === 'date') {
      const dateInput = (
        <DateInput
          ariaLabel={rule.field_label}
          value={toDateInput(rawValue)}
          disabled={busy}
          onChange={v => void save(rule.field, v || null)}
          style={INPUT_STYLE}
        />
      );
      // Termine (Anamnese, Beratungsgespräch): Datum + Uhrzeit in einem Feld,
      // gespeichert in beide Spalten (00:00 = ganztägig → Uhrzeit NULL).
      const timeField = TERMIN_TIME_FIELDS[rule.field as TerminDateField];
      if (!timeField) return dateInput;
      const timeValue = toTimeInput((merged as unknown as Record<string, unknown>)?.[timeField]);
      const dateValue = toDateInput(rawValue);
      return (
        <DateTimeInput
          ariaLabel={rule.field_label}
          date={dateValue}
          time={timeValue}
          disabled={busy || savingField === timeField}
          onCommit={v => {
            const { date, time } = splitDateTime(v);
            const patch: Record<string, unknown> = {};
            if (date !== (dateValue || null)) patch[rule.field] = date;
            if (time !== (timeValue || null)) patch[timeField] = time;
            void saveMany(patch);
          }}
          style={INPUT_STYLE}
        />
      );
    }

    // value-Felder: Entwurf lokal, speichern auf Blur/Enter.
    const draft = drafts[rule.field] ?? (rawValue != null ? String(rawValue) : '');
    const commit = () => {
      const current = rawValue != null ? String(rawValue) : '';
      if (draft !== current) void save(rule.field, draft === '' ? null : draft);
    };
    return (
      <input
        aria-label={rule.field_label}
        type="text"
        value={draft}
        disabled={busy}
        placeholder={rule.operator === 'equals' ? String(rule.value ?? '') : ''}
        onChange={e => setDrafts(d => ({ ...d, [rule.field]: e.target.value }))}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') commit(); }}
        style={INPUT_STYLE}
      />
    );
  };

  return (
    <section>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {phasesWithRules.map(phase => {
          const phaseRules = rules.filter(r => r.phase_id === phase.id);
          const fulfilled = phaseRules.filter(isFulfilled).length;
          return (
            <div key={phase.id} style={{ background: t.cardMuted, border: `1px solid ${t.borderSoft}`, borderRadius: 10, padding: '10px 14px' }}>
              {/* Kopfzeile: "Bedingungen" + Stage-Name in einer Zeile */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: t.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {tr('care.phase_rules.heading')}
                </span>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: phase.color || '#6b7280', flexShrink: 0 }} />
                <span style={{ fontSize: 12.5, fontWeight: 600, color: t.heading, flex: 1 }}>{phase.title}</span>
                <span style={{ fontSize: 10.5, fontWeight: 600, color: t.muted }}>{tr('care.phase_rules.next_stage')}</span>
                <span style={{ fontSize: 11, color: fulfilled === phaseRules.length ? '#039855' : t.muted, fontVariantNumeric: 'tabular-nums' }}>
                  {fulfilled}/{phaseRules.length}
                </span>
              </div>
              {/* Felder nebeneinander — feste, kompakte Breite, zentriert. */}
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 10 }}>
                {phaseRules.map(rule => {
                  const ok = isFulfilled(rule);
                  return (
                    <div key={rule.id} style={{ display: 'flex', flexDirection: 'column', gap: 4, width: TERMIN_TIME_FIELDS[rule.field as TerminDateField] ? 230 : 165 }}>
                      <span
                        title={describeRule(rule, tr)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 5,
                          fontSize: 11, color: ok ? t.text : t.muted,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                      >
                        <span
                          aria-label={ok ? tr('care.phase_rules.fulfilled') : tr('pflege.absences.open')}
                          style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 13, height: 13, borderRadius: '50%', flexShrink: 0,
                            fontSize: 9, fontWeight: 700,
                            background: ok ? '#d1fadf' : t.chipBg,
                            color: ok ? '#039855' : t.muted,
                          }}
                        >
                          {ok ? '✓' : '✗'}
                        </span>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{rule.field_label}</span>
                        {savingField === rule.field && <span style={{ color: t.muted }}>…</span>}
                      </span>
                      {renderInput(rule)}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
        {error && <div style={{ fontSize: 12, color: '#b42318' }}>{error}</div>}
      </div>
    </section>
  );
}
